import React, { Suspense, useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import { NoDataBoundary } from '@/shared/ui/NoDataBoundary';
import { SuspenseFallback } from '@/shared/ui/SuspenseFallback';

import { usePlatformLineage } from '@/features/lineage';
import type { PlatformLineageView } from '@/features/lineage';

import { PlatformLineageViewer } from '@/widgets/lineage';

import styles from './LineagePage.module.css';

const VALID_VIEWS: PlatformLineageView[] = ['service', 'domain', 'dataProduct'];

export const LineagePage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const currentView = useMemo(() => {
    const viewParam = searchParams.get('view') as PlatformLineageView | null;
    return viewParam && VALID_VIEWS.includes(viewParam) ? viewParam : 'service';
  }, [searchParams]);

  const { data, isLoading } = usePlatformLineage({
    view: currentView,
    upstreamDepth: 2,
    downstreamDepth: 2,
    includeDeleted: false,
    size: 50,
  });

  const handleViewChange = useCallback(
    (view: PlatformLineageView) => {
      setSearchParams({ view });
    },
    [setSearchParams]
  );

  const handleToggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  return (
    <div className={styles.lineagePage}>
      {!isFullscreen && (
        <div className={styles.header}>
          <div className={styles.title}>계보</div>
          <div className={styles.description}>
            데이터 플랫폼 전체의 계보 관계를 시각화하여 서비스, 도메인, 데이터 제품 간의 연결 관계를 한눈에 파악할 수
            있습니다.
          </div>
        </div>
      )}

      <NoDataBoundary data={data} emptyDescription="계보 데이터가 없습니다">
        <div className={styles.content}>
          <ErrorBoundary>
            <Suspense fallback={<SuspenseFallback />}>
              <PlatformLineageViewer
                lineageData={data}
                isLoading={isLoading}
                isFullscreen={isFullscreen}
                onToggleFullscreen={handleToggleFullscreen}
                selectedView={currentView}
                onViewChange={handleViewChange}
              />
            </Suspense>
          </ErrorBoundary>
        </div>
      </NoDataBoundary>
    </div>
  );
};
